/**
 * Which overlays are open — view state, not sim state (M1.1). The economy
 * overlays are summoned over the live floor from the top bar; the handbook
 * opens contextually from any overlay's `?`. A tiny external store in the
 * shape of `store.ts`, flushed on every change because a click is not a
 * frame.
 */

import { useSyncExternalStore } from 'react';

export type EconomyPanel = 'dashboard' | 'technology' | 'markets' | 'line' | null;

/** The Technology overlay's tabs (M1.5): the ladder, the parts bin, the desk. */
export type TechnologyTab = 'process' | 'components' | 'designs';

export interface PanelState {
  economy: EconomyPanel;
  /** The handbook page open, or `null` when the handbook is closed. */
  handbook: string | null;
  /** The floor's own overlays (scoreboard, site & flow, build palette) shown. */
  floorPanels: boolean;
  /** The frame/boundary debug HUD and B0's raw controls — off by default, a button away. */
  debug: boolean;
  /** The settings menu (the gear) open. */
  settings: boolean;
  /** Which Technology tab is showing — remembered across close/open. */
  technologyTab: TechnologyTab;
}

let state: PanelState = {
  economy: null,
  handbook: null,
  floorPanels: true,
  debug: false,
  settings: false,
  technologyTab: 'process',
};
const listeners = new Set<() => void>();

export function panelState(): PanelState {
  return state;
}

export function setPanels(patch: Partial<PanelState>): void {
  state = { ...state, ...patch };
  for (const listener of listeners) listener();
}

export function subscribePanels(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function usePanels(): PanelState {
  return useSyncExternalStore(subscribePanels, panelState);
}

export function toggleEconomy(panel: Exclude<EconomyPanel, null>): void {
  setPanels({ economy: state.economy === panel ? null : panel });
}

export function openHandbook(page: string): void {
  setPanels({ handbook: page });
}

export function closeHandbook(): void {
  setPanels({ handbook: null });
}
